import React, { FunctionComponent, useEffect, useState } from 'react';
import Input from '@murphy-frontend/web-core/components/Input';
import Button from '@murphy-frontend/web-core/components/Button';
import Spinner from '@murphy-frontend/web-core/components/Spinner';
import { useCreateContactGroup, useCreateContactGroupWithUsers, useEditContactGroup } from '@murphy-frontend/common/api/ContactGroups/Mutations';
import SearchBar from '@murphy-frontend/web-core/components/SearchBar';
import { ContactModel } from '@murphy-frontend/common/interfaces/IContactsApi';
import { CustomCheckbox } from '../../../common/components/MurphyCheckbox';
import TableFooter from '@murphy-frontend/web-core/components/ColumnGroupTable/TableFooter';
import useWindowDimensions from '@murphy-frontend/web-core/hooks/useWindowDimensions';

interface AddContactGroupProps {
  customerId: string,
  onSuccess: () => void,
  onFail: () => void,
  onCancel?: () => void,
  groupId?: number,
  groupName?: string,
  contacts?: ContactModel[],
  allContacts: ContactModel[],
  translations: Record<string, string>,
}

const styles = {
  input: { borderWidth: 1, borderColor: '#EBEBEB', borderStyle: 'solid', borderRadius: 5, width: '40rem' },
  label: { width: '10rem' }
};

const AddContactGroup: FunctionComponent<AddContactGroupProps> = ({
  customerId, onSuccess, onFail, translations, contacts = [], allContacts, groupId, groupName, onCancel
}) => {
  const [name, setName] = useState(groupName || '');
  const [isLoading, setIsLoading] = useState(false);
  const [nameErrorMessage, setNameErrorMessage] = useState('');
  const [contactsInGroup, setContactsInGroup] = useState<ContactModel[]>(contacts.map(c => ({ ...c, isAdded: true })));
  const [contactsNotInGroup, setContactsNotInGroup] = useState<ContactModel[]>(allContacts.map(c => ({ ...c, isAdded: false })));
  const [searchText, setSearchText] = useState('');
  const [mergedContacts, setMergedContacts] = useState<ContactModel[]>([]);
  const [selectAll, setSelectAll] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  const { mutate: createContactGroup, isPending: createContactIsLoading } = useCreateContactGroupWithUsers(customerId);
  const { mutateAsync: editContactGroup } = useEditContactGroup(groupId, customerId);
  const { currentSize: currentScreenSize } = useWindowDimensions();

  const onNameChanged = (event) => {
    if (!event.target.value) {
      setNameErrorMessage(translations.mandatory);
    } else {
      setNameErrorMessage('');
    }
    setName(event.target.value);
  };

  const onChangePage = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  // Update mergedContacts for display only (filtered by search)
  useEffect(() => {
    const allContactsCombined = [...contactsInGroup, ...contactsNotInGroup];
    const filteredContacts = allContactsCombined.filter(p =>
      p.name.toUpperCase().startsWith(searchText?.toUpperCase() || "")
    );
    setMergedContacts(filteredContacts);
  }, [contactsInGroup, contactsNotInGroup, searchText]);

  // Handle "Select All" for displayed contacts only
  useEffect(() => {
    if (mergedContacts?.length > 0) {
      const updatedMergedContacts = mergedContacts.map(contact => ({
        ...contact,
        isAdded: selectAll
      }));
      setMergedContacts(updatedMergedContacts);

      // Sync the original lists with the "Select All" action
      setContactsInGroup(prev =>
        prev.map(c => ({ ...c, isAdded: selectAll || c.isAdded }))
      );
      setContactsNotInGroup(prev =>
        prev.map(c => ({ ...c, isAdded: selectAll })));
    }
  }, [selectAll]);

  // Handle checkbox change
  const onCheckboxChange = (contactId: string | number, isAdded: boolean) => {
    const updateContactInList = (list: ContactModel[]) =>
      list.map(c => c.id === contactId || c.customeruserid === contactId ? { ...c, isAdded } : c);

    setContactsInGroup(prev => updateContactInList(prev));
    setContactsNotInGroup(prev => updateContactInList(prev));
  };

  const submit = (e) => {
    e.preventDefault();
    setIsLoading(true);

    // Use the original lists, not the filtered mergedContacts
    const allContactsCombined = [...contactsInGroup, ...contactsNotInGroup];
    const createContactGroupRequest = {
      Name: name,
      CustomerId: customerId,
      AddContanctIds: allContactsCombined
        .filter(contact => contact.isAdded && contact.type === "contact")
        .map(item => item.customeruserid || item.id),
      AddCustomerUserIds: allContactsCombined
        .filter(contact => contact.isAdded && contact.type === "user")
        .map(item => item.customeruserid || item.id)
    };

    if (groupId) {
      const editGroupRequest = {
        ID: groupId,
        ...createContactGroupRequest
      };
      editContactGroup(editGroupRequest, {
        onSuccess: () => {
          setIsLoading(false);
          onSuccess();
        },
        onError: () => {
          setIsLoading(false);
          onFail();
        },
      });
    } else {
      createContactGroup(createContactGroupRequest, {
        onSuccess: () => {
          setIsLoading(false);
          onSuccess();
        },
        onError: () => {
          setIsLoading(false);
          onFail();
        },
      });
    }
  };

  if (isLoading || createContactIsLoading) {
    return (<div className="spinner-container-global-center"><Spinner /></div>);
  }

  return (
    <form className="modal-form" style={{ width: '50rem' }}>
      <div className='form' style={{ width: '50rem' }}>
        <span style={styles.label}>{translations.grpname || "Group name"}</span>
        <Input style={styles.input} val={name} isRequired handleChange={onNameChanged} errorMessage={nameErrorMessage} />
      </div>

      <div className='form' style={{ width: '50rem' }}>
        <span style={styles.label}>{translations.addcontact || "Add contact"}</span>
        <div style={{ width: '40rem' }}>
          <SearchBar
            val={searchText}
            showSearchIcon
            handleChange={(event) => setSearchText(event.target.value)}
            placeholder={translations["placeholder-lang-search-action"]}
          />
        </div>
      </div>

      {!searchText && (
        <div style={{ flexDirection: 'row', display: 'flex', width: '18rem', marginBottom: 10, marginLeft: '10rem' }}>
          <CustomCheckbox
            onChange={(val) => setSelectAll(val)}
            checked={selectAll}
            checkedColor='#00A098'
            unCheckedColor='#7F7F7F'
          />
          <label style={{ maxWidth: '15rem', overflow: 'hidden' }}>{translations.markall || "Select All"}</label>
        </div>
      )}

      <div style={{ flexDirection: 'row', flexWrap: 'wrap', display: 'flex', width: '40rem', marginLeft: '10rem', justifyContent: 'space-between' }}>
        {(searchText ? mergedContacts : mergedContacts.slice((currentPage - 1) * 10, currentPage * 10))?.map((contact) => (
          <div key={contact.id || contact.customeruserid} style={{ borderColor: '#EBEBEB', flexDirection: 'row', borderWidth: 1, borderStyle: 'solid', display: 'flex', paddingLeft: 10, paddingRight: 10, paddingTop: 10, paddingBottom: 10, width: '18rem', marginBottom: 10 }}>
            <CustomCheckbox
              onChange={(val) => onCheckboxChange(contact.id || contact.customeruserid, val)}
              checked={contact.isAdded}
              checkedColor='#00A098'
              unCheckedColor='#7F7F7F'
            />
            <label style={{ maxWidth: '15rem', overflow: 'hidden' }}>{contact.name}</label>
          </div>
        ))}
      </div>

      <TableFooter
        currentScreenSize={currentScreenSize}
        numberOfItems={mergedContacts?.length}
        currentSelectedPage={currentPage}
        numberOfItemsPerPage={10}
        onChangeNumberOfItemsPerPage={() => { }}
        onChangePage={onChangePage}
        hidePageInputs
        translations={translations}
      />

      <div style={{ flexDirection: 'row', display: 'flex', justifyContent: 'center' }}>
        {onCancel && (
          <div className="modal-footer">
            <Button buttonStyle="btn--primary" buttonSize="btn--medium" onClick={onCancel}>{translations.cancel}</Button>
          </div>
        )}
        <div className="modal-footer">
          <Button buttonStyle="btn--primary" buttonSize="btn--medium" onClick={submit}>{translations.save}</Button>
        </div>
      </div>
    </form>
  );
};

export default AddContactGroup;